import { useTranslation } from 'react-i18next';
import { Card } from '@/common/components/Card/Card';
import { CardActionDownload } from '@/common/components/CardActionDownload/CardActionDownload';
import { CardGroup } from '@/common/components/CardGroup/CardGroup';
import { Drawer } from '@/common/components/Drawer/Drawer';
import { Grid } from '@/common/components/Grid/Grid';
import { LabeledValue } from '@/common/components/LabeledValue/LabeledValue';
import { Stack } from '@/common/components/Stack/Stack';
import { MARKET } from '@/common/constants/markets';
import { useMarket } from '@/common/hooks/useMarket';
import { IFamilyMember } from '@/common/integrations/menhir/employee/employee/employee.types';
import { getMemberIconByAge } from '@/common/utils/family-members';

export type TFamilyMemberDrawerProps = {
  member: IFamilyMember;
  onClose: () => void;
};

export function FamilyMemberDrawer({
  member,
  onClose,
}: TFamilyMemberDrawerProps) {
  const { t } = useTranslation();
  const { slug } = useMarket();
  return (
    <Drawer
      title={member.shortName}
      icon={getMemberIconByAge(member.birthdate)}
      iconBadgeColor="accentSecondary20"
      onClose={onClose}
    >
      <Stack gap="section">
        <Card plain>
          <Stack gap="section">
            <LabeledValue
              label={t('dashboard.family_member_details.fields.full_name')}
              value={member.fullName}
            />
            <Grid columnBasis="basis-[13.4rem]" gap="section">
              <LabeledValue
                label={t('dashboard.family_member_details.fields.kinship')}
                value={t(`general.kinship.${member.type}`)}
              />
              <LabeledValue
                label={t('dashboard.family_member_details.fields.tax_id')}
                value={member.taxId?.value ?? '-'}
              />
              {member.gender && (
                <LabeledValue
                  label={t('dashboard.family_member_details.fields.gender')}
                  value={t(`general.gender.${member.gender}`)}
                />
              )}
              <LabeledValue
                label={t(
                  'dashboard.family_member_details.fields.birthday_date'
                )}
                value={member.birthdate}
              />
              {slug === MARKET.IT && (
                <LabeledValue
                  label={t('dashboard.family_member_details.fields.dependant')}
                  value={
                    member.dependant
                      ? t('dashboard.family_member_details.values.yes')
                      : t('dashboard.family_member_details.values.no')
                  }
                />
              )}
              {slug === MARKET.IT && (
                <LabeledValue
                  label={t(
                    'dashboard.family_member_details.fields.non_sufficient'
                  )}
                  value={
                    member.nonSufficient
                      ? t('dashboard.family_member_details.values.yes')
                      : t('dashboard.family_member_details.values.no')
                  }
                />
              )}
            </Grid>
          </Stack>
        </Card>
        {member.files.length > 0 && (
          <CardGroup size="sm" gap="2xs">
            {member.files.map((file) => (
              <CardActionDownload
                key={file.id}
                fileUrl={file.url ?? ''}
                title={t(
                  'dashboard.add_family_member.fields.kinship_degree_proof.title'
                )}
              />
            ))}
          </CardGroup>
        )}
      </Stack>
    </Drawer>
  );
}
